import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { STATUS_LABEL, DOC_TYPES } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, FileText, Clock, CheckCircle2, XCircle, Download, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const toneClass = {
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  red: 'bg-rose-100 text-rose-800 border-rose-200',
};

const ApplicantDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/applications').then(({ data }) => setApps(data)).catch(() => toast.error('Failed to load applications')).finally(() => setLoading(false));
  }, []);

  const stats = {
    total: apps.length,
    pending: apps.filter((a) => a.status === 'pending_jakrikra' || a.status === 'forwarded_nokma').length,
    approved: apps.filter((a) => a.status === 'approved').length,
    rejected: apps.filter((a) => a.status === 'rejected').length,
  };

  const downloadApproval = (app) => {
    const html = `<html><head><title>Approval ${app.approvalCode}</title>
      <style>body{font-family:Georgia,serif;background:#FBF7F0;color:#2A1F14;padding:48px;}
      .card{max-width:720px;margin:auto;border:6px double #8B6F47;padding:48px;background:#fff;}
      h1{text-align:center;color:#8B6F47;margin:0;}h2{text-align:center;font-style:italic;color:#2D5016;margin:8px 0 24px;}
      .row{margin:10px 0;}strong{color:#6B1B1F;}.code{font-family:monospace;background:#F4ECDC;padding:6px 12px;border-radius:4px;}</style>
      </head><body><div class='card'><h1>Nokma Sani Union</h1><h2>Ku’monganio Bil · Garo Hills District</h2>
      <hr/><h3 style='text-align:center;'>${app.typeLabel}</h3>
      <div class='row'><strong>Applicant:</strong> ${app.applicantName}</div>
      <div class='row'><strong>Application ID:</strong> ${app.id}</div>
      <div class='row'><strong>Approval Code:</strong> <span class='code'>${app.approvalCode}</span></div>
      <div class='row'><strong>A’khing:</strong> ${app.akhing}</div>
      <div class='row'><strong>Purpose:</strong> ${app.purpose || '—'}</div>
      ${app.days ? `<div class='row'><strong>Validity:</strong> ${app.days} days</div>` : ''}
      <div class='row'><strong>Approved By:</strong> ${app.approvedBy}</div>
      <div class='row'><strong>Approved On:</strong> ${app.approvedAt}</div>
      <p style='margin-top:32px;text-align:center;font-style:italic;color:#2D5016;'>Issued under the traditional authority of the Nokma Sani Union.</p>
      </div></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    toast.success('Approval certificate opened. Use Print > Save as PDF.');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-sm text-muted-foreground">Welcome back</div>
            <h1 className="font-serif text-3xl font-bold">{user.name}</h1>
            <div className="text-sm text-muted-foreground mt-1">{user.applicantAddress || user.address}</div>
          </div>
          <Button onClick={() => navigate('/applicant/new')} className="bg-primary hover:bg-primary/90 gap-2 shadow-warm">
            <Plus className="h-4 w-4" /> New Application
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { l: 'Total', v: stats.total, icon: FileText, c: 'text-primary' },
            { l: 'Pending', v: stats.pending, icon: Clock, c: 'text-amber-600' },
            { l: 'Approved', v: stats.approved, icon: CheckCircle2, c: 'text-emerald-600' },
            { l: 'Rejected', v: stats.rejected, icon: XCircle, c: 'text-rose-600' },
          ].map((s) => (
            <Card key={s.l} className="border-border">
              <CardContent className="pt-6 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg bg-secondary flex items-center justify-center ${s.c}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{s.v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-serif">My Applications</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-16 text-muted-foreground">Loading...</div>
            ) : apps.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <div className="text-muted-foreground">No applications yet</div>
                <Button onClick={() => navigate('/applicant/new')} className="mt-4 bg-primary hover:bg-primary/90">Create your first application</Button>
              </div>
            ) : (
              <div className="space-y-4">
                {apps.map((a) => {
                  const tone = STATUS_LABEL[a.status];
                  const docMeta = DOC_TYPES.find((d) => d.id === a.type);
                  return (
                    <div key={a.id} className="border border-border rounded-lg p-5 hover:shadow-warm transition-shadow bg-card">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">{a.id}</Badge>
                            <Badge variant="outline" className={`${toneClass[tone.tone]}`}>{tone.label}</Badge>
                          </div>
                          <div className="font-serif text-lg font-semibold mt-2">{a.typeLabel}</div>
                          <div className="text-sm text-muted-foreground">{docMeta?.subtitle}</div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {a.akhing}</span>
                            {a.days && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {a.days} days</span>}
                            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {(a.createdAt || '').slice(0,10)}</span>
                          </div>
                        </div>
                        {a.status === 'approved' && (
                          <Button variant="outline" onClick={() => downloadApproval(a)} className="gap-2 border-primary/30">
                            <Download className="h-4 w-4" /> Certificate
                          </Button>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex flex-wrap gap-2">
                          {(a.timeline || []).map((t, i) => (
                            <div key={i} className="text-xs flex items-center gap-1.5 text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                              <span className="font-medium text-foreground">{t.stage.replace(/_/g, ' ')}</span> · <span>{(t.at || '').slice(0,16).replace('T',' ')}</span> · <span>{t.by}</span>
                              {i < a.timeline.length - 1 && <span className="mx-1 text-border">→</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default ApplicantDashboard;
